import { WorkspaceIntegration, WorkspaceIntegrationStatuses } from '@prisma/client';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from '@zond/ui';
import { ArrowDownAZ, ArrowUpAZ, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppCard } from './components/app-card/app-card';
import { apps } from './data/apps';
import { useGetMyIntegrations } from './integrations.queries';

export default function Apps() {
  const { t } = useTranslation('integrations');
  const [sort, setSort] = useState('ascending');
  const [appType, setAppType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { data: connectedIntegrations, isLoading } = useGetMyIntegrations();
  const connectedAppNames = new Set(
    connectedIntegrations
      ?.filter(
        (integration: WorkspaceIntegration) => integration.status === WorkspaceIntegrationStatuses.Enable
      )
      .map((integration: WorkspaceIntegration) => integration.integrationType.toLowerCase()) || []
  );

  const filteredApps = apps
    .sort((a, b) => {
      // First, sort by availability
      if (a.isAvailableToPublic !== b.isAvailableToPublic) {
        return a.isAvailableToPublic ? -1 : 1; // Available applications go first
      }
      // Then by connection status (if both are available or both are unavailable)
      const aConnected = connectedAppNames.has(a.name.toLowerCase());
      const bConnected = connectedAppNames.has(b.name.toLowerCase());
      if (aConnected !== bConnected) {
        return aConnected ? -1 : 1; // Connected applications go first
      }
      // Finally, sort by name
      return sort === 'ascending' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    })
    .filter((app) =>
      appType === 'connected'
        ? connectedAppNames.has(app.name.toLowerCase())
        : appType === 'notConnected'
          ? !connectedAppNames.has(app.name.toLowerCase())
          : true
    )
    .filter((app) => app.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="flex flex-col h-[calc(100dvh-64px)]">
      <div className="flex-none p-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('grid.title')}</h1>
          <p className="text-muted-foreground">{t('grid.subtitle')}</p>
        </div>
        <div className="my-4 flex items-end justify-between sm:my-0 sm:items-center">
          <div className="flex flex-col gap-4 sm:my-4 sm:flex-row">
            <Input
              placeholder={t('grid.filterPlaceholder')}
              className="h-9 w-40 lg:w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select value={appType} onValueChange={setAppType}>
              <SelectTrigger className="w-36">
                <SelectValue>{t(`grid.filter.${appType}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('grid.filter.all')}</SelectItem>
                <SelectItem value="connected">{t('grid.filter.connected')}</SelectItem>
                <SelectItem value="notConnected">{t('grid.filter.notConnected')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-16">
              <SelectValue>
                <SlidersHorizontal size={18} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="ascending">
                <div className="flex items-center gap-4">
                  <ArrowUpAZ size={16} />
                  <span>{t('grid.sort.ascending')}</span>
                </div>
              </SelectItem>
              <SelectItem value="descending">
                <div className="flex items-center gap-4">
                  <ArrowDownAZ size={16} />
                  <span>{t('grid.sort.descending')}</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator className="shadow" />
      </div>
      <main className="grow overflow-auto custom-scrollbar">
        {isLoading ? (
          <div className="p-6">
            <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array(apps.length)
                .fill(0)
                .map((_, index) => (
                  <li
                    key={index}
                    className="relative rounded-lg border bg-card text-card-foreground shadow-sm"
                  >
                    <div className="flex flex-col space-y-4 p-6">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-12 rounded-full" />
                        <Skeleton className="h-6 w-32" />
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="mt-2 h-9 w-28" />
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center text-center">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('grid.empty.title')}</p>
            <p className="mt-1 text-sm text-gray-500">{t('grid.empty.description')}</p>
          </div>
        ) : (
          <ul className="grid gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredApps.map((app) => (
              <AppCard
                key={app.name}
                name={app.name}
                description={app.desc}
                logoUrl={app.logoUrl}
                isConnected={connectedAppNames.has(app.name.toLowerCase())}
                isAvailableToPublic={app.isAvailableToPublic}
              />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
